<template>
  <div class="auth-modal-backdrop" @click.self="$emit('close')">

    <!-- Floating "De ce e necesar" popup -->
    <div v-if="showWhyNotice" class="auth-modal__why-overlay" @click.self="showWhyNotice = false">
      <div class="auth-modal__why-dialog" role="dialog" aria-modal="true">
        <button type="button" class="auth-modal__why-dialog-close" @click="showWhyNotice = false" aria-label="Închide">×</button>
        <p class="auth-modal__why-dialog-title">De ce e necesar un cont pentru a vota?</p>
        <p class="auth-modal__why-dialog-body">
          Pentru a participa la sondaje, este necesar un cont în scopul corelării statistice a răspunsurilor.
          Toate datele sunt păstrate <strong>anonim</strong> — nu colectăm informații de identificare personală.
          Contul tău ne permite să corelăm răspunsurile cu parametri demografici anonimi (vârstă, județ, ocupație)
          și să oferim statistici mai relevante. Nu stocăm date de identificare personală și nu vindem informații
          despre utilizatori.
        </p>
      </div>
    </div>

    <div class="auth-modal" role="dialog" aria-modal="true" aria-labelledby="auth-modal-title">
      <button type="button" class="auth-modal__close" @click="$emit('close')" aria-label="Închide">×</button>

      <div class="auth-card__tabs">
        <button
          type="button"
          class="auth-card__tab"
          :class="{ 'auth-card__tab--active': activeTab === 'login' }"
          @click="activeTab = 'login'"
        >
          Autentificare
        </button>
        <button
          type="button"
          class="auth-card__tab"
          :class="{ 'auth-card__tab--active': activeTab === 'register' }"
          @click="activeTab = 'register'"
        >
          Cont nou
        </button>
      </div>

      <!-- Login social -->
      <div class="auth-social">
        <a class="auth-social__btn auth-social__btn--google" href="/api/auth/google/redirect" @click="rememberReturnPath"><i class="mdi mdi-google" aria-hidden="true"></i> Continuă cu Google</a>
        <a class="auth-social__btn auth-social__btn--facebook" href="/api/auth/facebook/redirect" @click="rememberReturnPath"><i class="mdi mdi-facebook" aria-hidden="true"></i> Continuă cu Facebook</a>
        <div class="auth-social__divider"><span>sau</span></div>
      </div>

      <!-- Login -->
      <form v-if="activeTab === 'login'" class="auth-form" @submit.prevent="submitLogin">
        <FormField
          v-model="loginForm.login"
          label="Email sau username"
          placeholder="ionescu sau ion@exemplu.ro"
          autocomplete="username"
          :errors="validationErrors.login"
        />
        <FormField
          v-model="loginForm.password"
          type="password"
          label="Parolă"
          placeholder="••••••••"
          autocomplete="current-password"
          :errors="validationErrors.password"
        />
        <div v-if="showNotice" class="auth-modal__why-notice">
          <button
            type="button"
            class="auth-modal__why-btn"
            @click="showWhyNotice = true"
          >
            De ce e necesar să mă autentific pentru a vota?
          </button>
        </div>
        <button type="submit" class="auth-form__submit" :disabled="isSubmitting">
          {{ isSubmitting ? 'Se procesează...' : 'Intră în cont' }}
        </button>
      </form>

      <!-- Register -->
      <form v-else class="auth-form" @submit.prevent="submitRegister">
        <div v-if="showNotice" class="auth-modal__why-notice">
          <button
            type="button"
            class="auth-modal__why-btn"
            @click="showWhyNotice = true"
          >
            De ce e necesar să mă autentific pentru a vota?
          </button>
        </div>
        <p v-if="switchedToRegister" class="register-new-account-hint">
          Se pare că nu ai cont, hai să îți facem unul.
        </p>
        <div class="register-data-notice">
          Nu cerem informații personale. Totuși, câțiva parametri demografici ne ajută la clasificarea statistică a răspunsurilor.
        </div>
        <FormField
          v-model="registerForm.username"
          label="Username"
          placeholder="ionescu_ion"
          :errors="validationErrors.username"
        />
        <FormField
          v-model="registerForm.email"
          type="email"
          label="Email"
          placeholder="ion@exemplu.ro"
          :errors="validationErrors.email"
        >
          <template #label>Email <em style="font-weight: 400; font-style: italic">(opțional)</em></template>
        </FormField>
        <FormField
          v-model="registerForm.password"
          type="password"
          label="Parolă"
          placeholder="••••••••"
          :errors="validationErrors.password"
        />
        <FormField
          v-model="registerForm.gender"
          type="select"
          label="Gen"
          :options="genderOptions"
          :errors="validationErrors.gender"
        />
        <FormField
          v-model="registerForm.age_group"
          type="select"
          label="Grupă de vârstă"
          :options="ageGroupOptions"
          :errors="validationErrors.age_group"
        />
        <FormField
          v-model="registerForm.region_code"
          type="select"
          label="Județ"
          :options="regionCodeOptions"
          :errors="validationErrors.region_code"
        />
        <SearchLookupDropdown
          v-model="registerForm.ocupation_id"
          endpoint="/lookups/ocupations"
          label="Ocupație"
          placeholder="Caută ocupația"
          :errors="validationErrors.ocupation_id"
        />
        <SearchLookupDropdown
          v-model="registerForm.education_id"
          endpoint="/lookups/educations"
          label="Educație"
          placeholder="Caută nivelul de educație"
          :errors="validationErrors.education_id"
        />

        <!-- Math captcha -->
        <div class="captcha-widget">
          <div class="captcha-widget__label">Introdu codul de verificare</div>
          <div class="captcha-widget__math-row">
            <span class="captcha-widget__math-question">Câte este {{ mathCaptcha.a }} + {{ mathCaptcha.b }} ?</span>
            <button
              type="button"
              class="captcha-widget__refresh"
              aria-label="Regenerează codul de verificare"
              @click="generateMathCaptcha"
            >↻</button>
          </div>
          <FormField
            v-model="mathCaptcha.answer"
            type="number"
            label=""
            placeholder="Scrie rezultatul aici"
            autocomplete="off"
            inputmode="numeric"
            :errors="mathCaptchaError ? [mathCaptchaError] : (validationErrors.captcha_value || [])"
          />
        </div>

        <button type="submit" class="auth-form__submit" :disabled="isSubmitting">
          {{ isSubmitting ? 'Se procesează...' : 'Creează cont' }}
        </button>
      </form>

      <div v-if="errorMessage" class="auth-card__error">{{ errorMessage }}</div>
    </div>
  </div>
</template>

<script>
import FormField from './FormField.vue';
import SearchLookupDropdown from './SearchLookupDropdown.vue';
import recaptchaMixin from '../mixins/recaptchaMixin.js';

export default {
  name: 'AuthModal',

  components: { FormField, SearchLookupDropdown },

  mixins: [recaptchaMixin],

  props: {
    showNotice: {
      type: Boolean,
      default: true,
    },
  },

  emits: ['close', 'success'],

  data() {
    return {
      activeTab: 'login',
      isSubmitting: false,
      errorMessage: '',
      validationErrors: {},
      switchedToRegister: false,
      showWhyNotice: false,
      mathCaptcha: { a: 0, b: 0, answer: '' },
      mathCaptchaError: null,
      loginForm: {
        login: '',
        password: '',
      },
      registerForm: {
        username: '',
        email: '',
        password: '',
        gender: '',
        age_group: '',
        region_code: '',
        ocupation_id: null,
        education_id: null,
      },
      genderOptions: [
        { value: '', label: '— Selectează gen —' },
        { value: 'm', label: 'Bărbat' },
        { value: 'f', label: 'Femeie' },
        { value: 'n', label: 'Nespecificat' },
      ],
      ageGroupOptions: [
        { value: '', label: '— Selectează —' },
        { value: 'under_14', label: 'Sub 14 ani' },
        { value: '14_18', label: '14 – 18 ani' },
        { value: '18_24', label: '18 – 24 ani' },
        { value: '24_30', label: '24 – 30 ani' },
        { value: '30_40', label: '30 – 40 ani' },
        { value: '40_65', label: '40 – 65 ani' },
        { value: 'over_65', label: 'Peste 65 ani' },
      ],
      regionCodeOptions: [
        { value: '', label: '— Selectează județ —' },
        { value: 'AB', label: 'AB – Alba' },
        { value: 'AG', label: 'AG – Argeș' },
        { value: 'AR', label: 'AR – Arad' },
        { value: 'B', label: 'B – București' },
        { value: 'BC', label: 'BC – Bacău' },
        { value: 'BH', label: 'BH – Bihor' },
        { value: 'BN', label: 'BN – Bistrița-Năsăud' },
        { value: 'BR', label: 'BR – Brăila' },
        { value: 'BT', label: 'BT – Botoșani' },
        { value: 'BV', label: 'BV – Brașov' },
        { value: 'BZ', label: 'BZ – Buzău' },
        { value: 'CJ', label: 'CJ – Cluj' },
        { value: 'CL', label: 'CL – Călărași' },
        { value: 'CS', label: 'CS – Caraș-Severin' },
        { value: 'CT', label: 'CT – Constanța' },
        { value: 'CV', label: 'CV – Covasna' },
        { value: 'DB', label: 'DB – Dâmbovița' },
        { value: 'DJ', label: 'DJ – Dolj' },
        { value: 'GJ', label: 'GJ – Gorj' },
        { value: 'GL', label: 'GL – Galați' },
        { value: 'GR', label: 'GR – Giurgiu' },
        { value: 'HD', label: 'HD – Hunedoara' },
        { value: 'HR', label: 'HR – Harghita' },
        { value: 'IF', label: 'IF – Ilfov' },
        { value: 'IL', label: 'IL – Ialomița' },
        { value: 'IS', label: 'IS – Iași' },
        { value: 'MH', label: 'MH – Mehedinți' },
        { value: 'MM', label: 'MM – Maramureș' },
        { value: 'MS', label: 'MS – Mureș' },
        { value: 'NT', label: 'NT – Neamț' },
        { value: 'OT', label: 'OT – Olt' },
        { value: 'PH', label: 'PH – Prahova' },
        { value: 'SB', label: 'SB – Sibiu' },
        { value: 'SJ', label: 'SJ – Sălaj' },
        { value: 'SM', label: 'SM – Satu Mare' },
        { value: 'SV', label: 'SV – Suceava' },
        { value: 'TL', label: 'TL – Tulcea' },
        { value: 'TM', label: 'TM – Timiș' },
        { value: 'TR', label: 'TR – Teleorman' },
        { value: 'VL', label: 'VL – Vâlcea' },
        { value: 'VN', label: 'VN – Vrancea' },
        { value: 'VS', label: 'VS – Vaslui' },
      ],
    };
  },

  mounted() {
    document.addEventListener('keydown', this.onKeydown);
    this.generateMathCaptcha();
  },

  beforeUnmount() {
    document.removeEventListener('keydown', this.onKeydown);
  },

  watch: {
    activeTab(val) {
      this.showWhyNotice = false;
      if (val === 'register') {
        this.generateMathCaptcha();
      } else {
        this.switchedToRegister = false;
      }
    },
  },

  methods: {
    onKeydown(e) {
      if (e.key === 'Escape') {
        if (this.showWhyNotice) {
          this.showWhyNotice = false;
        } else {
          this.$emit('close');
        }
      }
    },

    normalizeError(error, fallback) {
      const responseErrors = error?.response?.data?.errors;
      if (responseErrors && typeof responseErrors === 'object') {
        this.validationErrors = responseErrors;
        const first = Object.values(responseErrors)[0];
        if (Array.isArray(first) && first.length > 0) return first[0];
      }
      return error?.response?.data?.message || fallback;
    },

    rememberReturnPath() {
      try {
        window.localStorage.setItem('auth_return_path', this.$route.fullPath);
      } catch {
        // localStorage indisponibil — după login revenim pe pagina principală
      }
    },

    storeToken(token) {
      if (!token) return;
      window.localStorage.setItem('auth_token', token);
      window.axios.defaults.headers.common.Authorization = `Bearer ${token}`;
    },

    async submitLogin() {
      this.isSubmitting = true;
      this.errorMessage = '';
      this.validationErrors = {};
      try {
        const recaptchaToken = await this.getRecaptchaToken('login');
        const response = await window.axios.post('auth/login', {
          ...this.loginForm,
          recaptcha_token: recaptchaToken,
        });
        this.storeToken(response.data?.token);
        this.$emit('success', { user: response.data?.user, token: response.data?.token });
      } catch (error) {
        const data = error?.response?.data || {};
        const status = error?.response?.status;
        // 404 = user not found (primary); user_not_found flag = explicit signal fallback
        const isUserNotFound = status === 404 || data.user_not_found === true;
        if (isUserNotFound) {
          const loginInput = this.loginForm.login.trim();
          const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(loginInput);
          this.registerForm.username = '';
          this.registerForm.email = '';
          if (isEmail) {
            this.registerForm.email = loginInput;
          } else {
            this.registerForm.username = loginInput;
          }
          this.switchedToRegister = true;
          this.activeTab = 'register';
          this.errorMessage = '';
          this.validationErrors = {};
        } else {
          this.errorMessage = this.normalizeError(error, 'Autentificarea a eșuat.');
        }
      } finally {
        this.isSubmitting = false;
      }
    },

    generateMathCaptcha() {
      this.mathCaptcha.a = Math.floor(Math.random() * 9) + 1;
      this.mathCaptcha.b = Math.floor(Math.random() * 9) + 1;
      this.mathCaptcha.answer = '';
      this.mathCaptchaError = null;
    },

    validateRegisterForm() {
      const errors = {};
      if (!this.registerForm.username.trim()) {
        errors.username = ['Username-ul este obligatoriu.'];
      }
      if (!this.registerForm.password) {
        errors.password = ['Parola este obligatorie.'];
      } else if (this.registerForm.password.length < 8) {
        errors.password = ['Parola trebuie să aibă minim 8 caractere.'];
      }
      if (!this.registerForm.gender) {
        errors.gender = ['Selectați genul.'];
      }
      if (!this.registerForm.age_group) {
        errors.age_group = ['Selectați grupa de vârstă.'];
      }
      if (!this.registerForm.region_code) {
        errors.region_code = ['Selectați județul.'];
      }
      if (!this.registerForm.ocupation_id) {
        errors.ocupation_id = ['Selectați ocupația.'];
      }
      if (!this.registerForm.education_id) {
        errors.education_id = ['Selectați nivelul de educație.'];
      }
      return errors;
    },

    async submitRegister() {
      this.mathCaptchaError = null;

      const frontendErrors = this.validateRegisterForm();
      if (Object.keys(frontendErrors).length > 0) {
        this.validationErrors = frontendErrors;
        this.errorMessage = 'Completați toate câmpurile obligatorii.';
        return;
      }

      const captchaAnswer = parseInt(this.mathCaptcha.answer, 10);
      if (isNaN(captchaAnswer) || captchaAnswer !== this.mathCaptcha.a + this.mathCaptcha.b) {
        this.generateMathCaptcha();
        this.mathCaptchaError = 'Codul de verificare este incorect.';
        return;
      }

      this.validationErrors = {};
      this.errorMessage = '';

      this.isSubmitting = true;
      const recaptchaToken = await this.getRecaptchaToken('register');
      const payload = {
        ...this.registerForm,
        email: this.registerForm.email || null,
        recaptcha_token: recaptchaToken,
      };
      try {
        const response = await window.axios.post('/auth/register', payload);
        this.storeToken(response.data?.token);
        this.$emit('success', { user: response.data?.user, token: response.data?.token });
      } catch (error) {
        this.errorMessage = this.normalizeError(error, 'Înregistrarea a eșuat.');
        this.generateMathCaptcha();
      } finally {
        this.isSubmitting = false;
      }
    },
  },
};
</script>
